/**
 * The thumbnail DJI Fly shows for a mission: the map around the polygon with the mission name
 * and its key figures in a band along the bottom.
 */
import { brand } from '@/theme/theme';

export const PREVIEW_WIDTH = 1600;
export const PREVIEW_HEIGHT = 752;
const FONT = "'Pangea', 'Plus Jakarta Sans', system-ui, sans-serif";

/** The largest centred crop of a `sw` x `sh` source that has the target's aspect ratio. */
export function coverRect(sw: number, sh: number, tw: number, th: number): { sx: number; sy: number; sw: number; sh: number } {
  const scale = Math.min(sw / tw, sh / th);
  const w = Math.round(tw * scale);
  const h = Math.round(th * scale);
  return { sx: Math.round((sw - w) / 2), sy: Math.round((sh - h) / 2), sw: w, sh: h };
}

/** `110 m · 80/75 % · 4,5 m/s · 147 snímků`, with non-breaking spaces before the units. */
export function previewLine(
  inputs: { altitude_m: number; forward_overlap: number; side_overlap: number },
  speedMs: number,
  photos: number,
  number: (n: number, digits?: number) => string,
  photosWord: string,
): string {
  return [`${number(inputs.altitude_m)}\u00a0m`, `${number(inputs.forward_overlap)}/${number(inputs.side_overlap)}\u00a0%`, `${number(speedMs, 1)}\u00a0m/s`, `${number(photos)}\u00a0${photosWord}`].join(' · ');
}

/** Draws the preview from a captured map canvas and encodes it as a JPEG. */
export function composePreview(source: HTMLCanvasElement, text: { title: string; line: string }): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = PREVIEW_WIDTH;
  canvas.height = PREVIEW_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.reject(new Error('canvas'));
  const crop = coverRect(source.width, source.height, PREVIEW_WIDTH, PREVIEW_HEIGHT);
  ctx.drawImage(source, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, PREVIEW_WIDTH, PREVIEW_HEIGHT);

  const bandTop = PREVIEW_HEIGHT - 300;
  const gradient = ctx.createLinearGradient(0, bandTop, 0, PREVIEW_HEIGHT);
  gradient.addColorStop(0, `${brand.text}00`);
  gradient.addColorStop(0.45, `${brand.text}cc`);
  gradient.addColorStop(1, `${brand.text}e6`);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, bandTop, PREVIEW_WIDTH, PREVIEW_HEIGHT - bandTop);

  ctx.fillStyle = brand.surface;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'center';
  ctx.font = `700 96px ${FONT}`;
  ctx.fillText(text.title, PREVIEW_WIDTH / 2, PREVIEW_HEIGHT - 130, PREVIEW_WIDTH - 120);
  ctx.font = `500 52px ${FONT}`;
  ctx.fillText(text.line, PREVIEW_WIDTH / 2, PREVIEW_HEIGHT - 48, PREVIEW_WIDTH - 120);

  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas'))), 'image/jpeg', 0.88));
}
